/**
 * Le morceau **d'origine**, préparé pour être écouté par fenêtres d'accord.
 *
 * Pur : ni React, ni audio. La lecture est ailleurs, dans
 * `use-chord-audition` ; ce qui est ici est la conversion, où elle se teste.
 *
 * ## Pourquoi l'original et pas le niveau
 *
 * Le morceau chargé est la réduction du niveau : une fondamentale à gauche,
 * une triade à droite, et rien d'autre. C'est ce qu'on demande aux mains, et
 * ce n'est pas ce qu'on veut à l'oreille. Le joueur doit entendre le passage
 * tel qu'il est écrit, puis poser dessus l'accord qu'on lui montre : c'est ce
 * rapprochement-là qui fait comprendre que sa triade est bien ce qu'il vient
 * d'entendre. Lui rejouer sa propre réduction ne lui apprendrait rien qu'il ne
 * soit déjà en train de faire.
 *
 * Les notes d'origine sont à portée sans rien recharger : l'analyse du morceau
 * reste en mémoire dans le studio, c'est elle dont `asAnalysis` ne remplace que
 * les notes pour fabriquer le niveau.
 *
 * ## Une seule piste, au piano
 *
 * Le format d'écoute est celui de la fenêtre d'import, donc il porte un
 * programme GM. Le banc qui joue cet extrait n'en a plus l'usage — c'est le
 * Salamander du piano-roll, un seul instrument (voir `use-chord-audition`) —
 * mais le champ reste renseigné à 0, le piano à queue : il appartient au
 * format, et le format est partagé.
 *
 * Une piste unique suffit : la séparation des mains est une information
 * d'analyse, pas une couleur de son.
 *
 * `densityBuckets` reste vide : le lecteur ne le lit pas, il n'est là que pour
 * les vignettes de la fenêtre d'import.
 */

import type { AnalysisNote, AuditionTrack } from "@/lib/music/analyzer-types";

/** Le programme GM du répertoire : piano à queue. */
const GRAND_PIANO = 0;

/**
 * À quelle vitesse le passage d'origine se fait entendre.
 *
 * Six dixièmes du tempo écrit. On n'écoute pas cet extrait pour l'admirer, on
 * pose une prise dessus pendant qu'il sonne : il faut le temps de reconnaître
 * l'accord, de trouver les touches et d'y arriver avant la fin de la fenêtre.
 * Au tempo réel, un changement toutes les deux noires ne laisse pas ce temps-là
 * à quelqu'un qui apprend, et le morceau file sans lui.
 *
 * Le ralenti est une dilatation des temps, pas un changement de lecture : les
 * échantillons sont déclenchés note par note, donc rien ne descend en hauteur.
 * Et il vaut pour **tout** ce qui est mesuré ici, la durée de la fenêtre
 * comprise : voir `auditionSeconds`, qui est la seule conversion, pour que la
 * fenêtre et les notes qu'elle contient ne puissent pas se désaccorder.
 */
export const AUDITION_RATE = 0.6;

/**
 * Un tick du morceau, en secondes d'écoute.
 *
 * L'unique conversion, utilisée par les notes de la piste comme par les bornes
 * de la fenêtre. Deux conversions séparées finiraient par diverger d'un
 * facteur, et la fenêtre s'arrêterait au milieu de ce qu'elle nomme.
 */
export function auditionSeconds(
  tickToSeconds: (tick: number) => number,
  tick: number,
): number {
  return tickToSeconds(tick) / AUDITION_RATE;
}

/**
 * De combien l'extrait passe devant le piano que le joueur touche.
 *
 * On joue **par-dessus** l'extrait, donc les deux sons se disputent la même
 * oreille au même instant, et c'est l'original qui doit gagner : c'est lui la
 * référence à laquelle la prise se compare.
 *
 * ## Pourquoi il vaut 1
 *
 * Il a valu 1,35, réglé à l'oreille, du temps où l'extrait sortait de la
 * banque General MIDI pendant que les doigts sortaient du Salamander. Les deux
 * échelles n'étaient alors reliées par rien, et le nombre ne compensait pas ce
 * qu'il croyait : mesuré sur les échantillons publiés des deux banques (C3, C4
 * et C5, RMS de la première seconde), le Salamander sort **11,4 dB au-dessus**
 * du piano FluidR3 à vélocité de déclenchement égale. Les 1,35 achetaient
 * +2,6 dB ; l'extrait passait donc, en réalité, près de 9 dB *sous* les doigts.
 *
 * Depuis que les deux viennent du même banc, le rapport est enfin calculable
 * et le multiplicateur *est* ce rapport : 1 met l'extrait exactement au niveau
 * d'une note jouée de même vélocité, soit ~9 dB plus haut qu'avant. Comme un
 * passage compte plus de voix qu'une prise, l'original gagne — pour la
 * première fois réellement, et de justesse, ce qui est l'ajustement qu'on veut
 * pour quelque chose qu'on recouvre.
 *
 * ## Pourquoi ce n'est plus un bouton de volume
 *
 * Sur un échantillonneur à couches, la vélocité n'est pas un gain : elle dit
 * de quelle force la note a été frappée, et choisit l'échantillon en
 * conséquence (`velocityToLayer`, sur le profil `high`). La pousser rendait
 * donc les attaques plus dures en plus de les monter, et écrasait les nuances
 * du haut contre le plafond de `Math.min(…, 1)`. À 1, l'extrait garde les
 * nuances écrites, ce qui est exactement ce qu'on lui demande de faire
 * entendre.
 *
 * Reste le seul endroit où changer cet équilibre.
 */
const AUDITION_GAIN = 1;

/**
 * Les notes du morceau, en millisecondes absolues depuis son début.
 *
 * Les temps passent par la carte de tempo du morceau et pas par un tempo
 * moyen : un ritardando écrit décalerait sinon l'extrait de plus en plus au fil
 * du morceau, et la fenêtre finirait par ne plus contenir l'accord qu'elle
 * nomme.
 */
export function pieceAuditionTrack(
  notes: readonly AnalysisNote[],
  tickToSeconds: (tick: number) => number,
): AuditionTrack {
  return {
    index: 0,
    gmProgram: GRAND_PIANO,
    isDrumChannel: false,
    densityBuckets: [],
    notes: notes
      .map((note) => {
        const startMs = auditionSeconds(tickToSeconds, note.tick) * 1000;

        return {
          midi: note.midi,
          startMs,
          durationMs: Math.max(
            auditionSeconds(tickToSeconds, note.tick + note.durationTicks) * 1000 - startMs,
            1,
          ),
          velocity: auditionVelocity(note.velocity),
        };
      })
      .sort((a, b) => a.startMs - b.startMs),
  };
}

/**
 * La vélocité, ramenée dans [0, 1] puis pesée par {@link AUDITION_GAIN}.
 *
 * L'analyseur rend du MIDI brut, le lecteur veut une fraction. La garde couvre
 * les deux conventions plutôt que de parier sur l'une : une vélocité de 0,8
 * lue comme du MIDI donnerait un accord inaudible, et 96 lu comme une fraction
 * saturerait tout.
 *
 * Le plafond ne sert plus qu'à borner une entrée aberrante : à gain 1, du MIDI
 * valide ne peut pas le toucher.
 */
function auditionVelocity(velocity: number): number {
  const normalized = velocity <= 1 ? Math.max(velocity, 0) : velocity / 127;

  return Math.min(normalized * AUDITION_GAIN, 1);
}
